import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, message } from 'antd';
import { useSaveBankMutation } from '../../services/settings.service';

const BankModal = ({ visible, bank, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [saveBank, { isLoading }] = useSaveBankMutation();

  useEffect(() => {
    if (visible) {
      if (bank) {
        // Editing existing bank
        form.setFieldsValue({
          name: bank.name,
          code: bank.code || '',
          branch: bank.branch || '',
          swiftCode: bank.swiftCode || '',
          ifscCode: bank.ifscCode || '',
          address: bank.address || '',
          isActive: bank.isActive !== undefined ? bank.isActive : true,
        });
      } else {
        // Adding new bank - set defaults
        form.setFieldsValue({
          name: '',
          code: '',
          branch: '',
          swiftCode: '',
          ifscCode: '',
          address: '',
          isActive: true,
        });
      }
    }
  }, [visible, bank, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        ...values,
        ...(bank?.id && { id: bank.id }),
      };

      await saveBank(payload).unwrap();

      message.success(
        `Bank ${bank ? 'updated' : 'created'} successfully`
      );

      form.resetFields();
      onSuccess();
    } catch (error) {
      if (error?.data?.message) {
        message.error(error.data.message);
      } else if (error?.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        message.error('Failed to save bank');
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={bank ? 'Edit Bank' : 'Add Bank'}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={isLoading}
      width={700}
      okText={bank ? 'Update' : 'Create'}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          name="name"
          label="Bank Name"
          rules={[
            { required: true, message: 'Please enter bank name' },
            { min: 3, message: 'Bank name must be at least 3 characters' },
            { max: 100, message: 'Bank name cannot exceed 100 characters' },
          ]}
        >
          <Input
            placeholder="e.g., State Bank of India, HDFC Bank"
            size="large"
          />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="code"
            label="Bank Code"
            rules={[
              { max: 20, message: 'Code cannot exceed 20 characters' },
            ]}
          >
            <Input
              placeholder="Bank code (optional)"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="branch"
            label="Branch"
            rules={[
              { max: 100, message: 'Branch cannot exceed 100 characters' },
            ]}
          >
            <Input
              placeholder="Branch name (optional)"
              size="large"
            />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            name="swiftCode"
            label="SWIFT Code"
            rules={[
              { max: 11, message: 'SWIFT code cannot exceed 11 characters' },
              {
                pattern: /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/i,
                message: 'Invalid SWIFT code format'
              },
            ]}
          >
            <Input
              placeholder="e.g., SBININBB123"
              size="large"
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>

          <Form.Item
            name="ifscCode"
            label="IFSC Code"
            rules={[
              { max: 11, message: 'IFSC code cannot exceed 11 characters' },
              {
                pattern: /^[A-Z]{4}0[A-Z0-9]{6}$/i,
                message: 'Invalid IFSC code format'
              },
            ]}
          >
            <Input
              placeholder="e.g., SBIN0001234"
              size="large"
              style={{ textTransform: 'uppercase' }}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="address"
          label="Address"
          rules={[
            { max: 500, message: 'Address cannot exceed 500 characters' },
          ]}
        >
          <Input.TextArea
            placeholder="Bank address (optional)"
            rows={3}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Active Status"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default BankModal;
